import React, {useEffect, useState} from 'react';
import {Button, Input, notification, Popconfirm, Select, Tabs} from 'antd';
import st from './Payments.module.scss';
import Table from '../../components/Table/Table';
import Uploader from "../../components/Uploader/Uploader";
import api from '../../config/axiosConfig';
import {DeleteOutlined, EditOutlined, ExportOutlined} from "@ant-design/icons";
import ModalForm from "../../components/ModalForm/ModalForm";
import {format, isWithinInterval, parseISO} from "date-fns";
import {DateRangeFilter, exportToExcel, FilterSearch, NumberRangeFilter, PaymentMethodFilter} from "../../utils/utils";

const {TabPane} = Tabs;

const Payments = () => {
    const [identifiers, setIdentifiers] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [paymentIsUpdated, setPaymentIsUpdated] = useState(false);
    const [currentPayment, setCurrentPayment] = useState(null);
    const [isSuccessfulPayments, setIsSuccessfulPayments] = useState(false);
    const [isAllPayments, setIsAllPayments] = useState(false);
    const [failedTableData, setFailedTableData] = useState([]);
    const [successfulTableData, setSuccessfulTableData] = useState([]);
    const [allTableData, setAllTableData] = useState([]);
    const [activeTab, setActiveTab] = useState('1'); // '1' for Failed, '2' for Successful

    const handleDataLoaded = (data, isSuccess, isAll = false) => {
        if (isAll) {
            setAllTableData(data);
        } else if (isSuccess) {
            setSuccessfulTableData(data);
        } else {
            setFailedTableData(data);
        }
    };

    const handleExport = () => {
        let dataToExport = [];

        if (activeTab === '1') {
            dataToExport = failedTableData;
        } else if (activeTab === '2') {
            dataToExport = successfulTableData;
        } else if (activeTab === '3') {
            dataToExport = allTableData;
        }

        if (!dataToExport || dataToExport.length === 0) {
            notification.error({
                message: 'Error',
                description: 'No data available to export.',
                placement: 'topRight',
            });
            return;
        }

        exportToExcel({
            data: dataToExport,
            columns,
            fileName: `Payments_Data_${activeTab}`,
            shouldAddTotal: true,
        });

        notification.success({
            message: 'Success',
            description: `Payments data exported successfully (${activeTab === '1'
                ? 'Failed'
                : activeTab === '2'
                    ? 'Successful'
                    : 'All'})`,
            placement: 'topRight',
        });
    };

    const formatDate = (isoDate) => {
        return format(new Date(isoDate), 'yyyy-MM-dd HH:mm:ss');
    };

    const baseColumns = [
        {
            title: 'თარიღი',
            dataIndex: 'issueDate',
            filterDropdown: DateRangeFilter,
            onFilter: (value, record) => {
                if (!record.issueDate || !value) return false;
                try {
                    const {start, end} = JSON.parse(value);
                    if (!start || !end) return true;

                    const recordDate = new Date(record.issueDate);
                    const startDate = parseISO(start);
                    const endDate = parseISO(end);

                    return isWithinInterval(recordDate, {start: startDate, end: endDate});
                } catch (err) {
                    return true;
                }
            },
            sorter: (a, b) => new Date(a.issueDate) - new Date(b.issueDate),
            width: 100,
        },
        {
            title: 'იდენთიფიკატორი',
            dataIndex: 'studentIdentifier',
            sorter: (a, b) => (a.studentIdentifier || '').localeCompare(b.studentIdentifier || ''),
            filterDropdown: ({
                                 setSelectedKeys,
                                 selectedKeys,
                                 confirm,
                                 clearFilters,
                             }) => (
                <FilterSearch
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    placeholder="Search Identifier"
                />
            ),
            onFilter: (value, record) => {
                return (record?.studentIdentifier.toLowerCase() || '').includes(value.toLowerCase());
            },
            width: 200,
        },
        {
            title: 'თანხა',
            dataIndex: 'amount',
            sorter: (a, b) => (Number(a.amount) || 0) - (Number(b.amount) || 0),
            filterDropdown: props => <NumberRangeFilter {...props} />,
            onFilter: (value, record) => {
                const {min, max} = JSON.parse(value);
                let minValue = min !== undefined ? min : -Infinity;
                let maxValue = max !== undefined ? max : Infinity;
                if (min !== undefined && max === undefined) {
                    maxValue = minValue;
                } else if (max !== undefined && min === undefined) {
                    minValue = maxValue;
                }
                if (minValue > maxValue) {
                    [minValue, maxValue] = [maxValue, minValue];
                }
                return record.amount >= minValue && record.amount <= maxValue;
            },
            width: 100,
        },
        {
            title: 'დანიშნულება',
            dataIndex: 'additionalData',
            sorter: (a, b) => (a.additionalData || '').localeCompare(b.additionalData || ''),
            filterDropdown: ({
                                 setSelectedKeys,
                                 selectedKeys,
                                 confirm,
                                 clearFilters,
                             }) => (
                <FilterSearch
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    placeholder="Search Data"
                />
            ),
            onFilter: (value, record) => {
                return (record?.additionalData.toLowerCase() || '').includes(value.toLowerCase());
            },
            width: 200,
        },
        {
            title: 'ატვირთვის თარიღი',
            dataIndex: 'createdAt',
            sorter: (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0),
            render: (date) => formatDate(date),
            filterDropdown: ({
                                 setSelectedKeys,
                                 selectedKeys,
                                 confirm,
                                 clearFilters,
                             }) => (
                <FilterSearch
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    placeholder="Search Date"
                />
            ),
            onFilter: (value, record) => {
                return (record?.createdAt.toLowerCase() || '').includes(value.toLowerCase());
            },
            width: 100,
        },
        {
            title: 'გადახდის მეთოდი',
            dataIndex: 'isTBCPay',
            width: 150,
            filterDropdown: activeTab !== '1' && PaymentMethodFilter,

            onFilter: (value, record) => {
                let isTBCPayValue = record.isTBCPay;
                if (typeof isTBCPayValue === 'string') {
                    isTBCPayValue = isTBCPayValue.toLowerCase() === 'true';
                }

                if (value === '') return true;
                if (value === 'true') {
                    return isTBCPayValue === true;
                }
                if (value === 'false') {
                    return isTBCPayValue === false;
                }
                return true;
            },

            sorter: (a, b) => (a.isTBCPay === b.isTBCPay ? 0 : a.isTBCPay ? -1 : 1),
            render: (isTBCPay) => {
                if (isTBCPay) {
                    return (
                        <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>
                            <img
                                src="/TBC.png"
                                alt="Payment Method"
                                style={{width: '20px'}}
                            />
                        </div>
                    );
                } else {
                    return (
                        <div>
                            ანგარიშის ნომრით
                        </div>
                    );
                }
            }
        }
    ];


    const actionsColumn = {
        title: 'Actions',
        key: 'actions',
        fixed: 'right',
        render: (text, record) => (
            <>
                <Button type="link" onClick={() => openEditModal(record)}>
                    <EditOutlined/>
                </Button>
                <Popconfirm
                    title="Are you sure you want to delete this payment?"
                    onConfirm={() => handleDeletePayment(record.id)}
                    okText="Yes"
                    cancelText="No"
                >
                    <Button type="link" danger>
                        <DeleteOutlined/>
                    </Button>
                </Popconfirm>
            </>
        ),
        width: 70,
    };

    const columns = (isSuccessfulPayments || isAllPayments) ? baseColumns : [...baseColumns, actionsColumn];

    const formItems = [
        [{label: 'იდენთიფიკატორი', name: 'studentIdentifier', component: <Input disabled={true}/>, span: 24}],
        [{
            label: 'მოსწავლე', name: 'identifier', component: (
                <Select
                    showSearch={true}
                    placeholder="Select a student"
                    filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={identifiers?.map(item => ({
                        value: item, // Use an identifier for value
                        label: item, // Use a human-readable label
                    }))}
                    allowClear={true}
                />
            ), span: 24
        },]
    ];

    const identifierList = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.get('students/identifiers', {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            setIdentifiers(response.data);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to fetch coaches list.',
                placement: 'topRight',
            });
        }
    };

    const handleEditPayment = async (values) => {
        setLoading(true);
        try {
            // Edit Payment
            const config = {
                headers: {
                    'Content-Type': 'text/plain'
                },
            };
            await api.put(`payments/${currentPayment.id}/edit-student-identifier`, values.identifier, config);
            notification.success({
                message: 'Success',
                description: 'Payment edited successfully',
                placement: 'topRight'
            });
            setIsModalOpen(false);
            setPaymentIsUpdated(!paymentIsUpdated);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to save team. Please try again.',
                placement: 'topRight'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDeletePayment = async (id) => {
        setLoading(true);
        try {
            await api.delete(`payments/${id}`);
            notification.success({
                message: 'Success',
                description: 'Payment has been successfully deleted.',
                placement: 'topRight',
            });
            setPaymentIsUpdated(prev => !prev);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to delete the payment. Please try again.',
                placement: 'topRight',
            });
        } finally {
            setLoading(false);
        }
    };

    const openEditModal = (payment) => {
        setCurrentPayment(payment);
        setIsModalOpen(true);
    };

    useEffect(() => {
        identifierList();
    }, []);

    return (
        <>
            <div className={st.btnContainer}>
                <Uploader/>
                <Button
                    type="default"
                    icon={<ExportOutlined/>}
                    onClick={handleExport}
                    style={{marginBottom: 16, marginLeft: 8}}
                >
                    Export to Excel
                </Button>
            </div>
            <ModalForm
                open={isModalOpen}
                title="Edit Payment"
                onCancel={() => {
                    setIsModalOpen(false)
                }}
                onSubmit={handleEditPayment}
                confirmLoading={loading}
                formItems={formItems}
                initialValues={currentPayment}
                rowIsUpdated={paymentIsUpdated}
            />
            <Tabs defaultActiveKey="1" onChange={(key) => {
                setActiveTab(key);
                setIsSuccessfulPayments(key === "2");
                setIsAllPayments(key === "3");
            }}>
                <TabPane tab="Failed Payments" key="1">
                    <Table columns={columns} url='payments?success=false&page=1'
                           rowIsUpdated={paymentIsUpdated}
                           onDataLoaded={(data) => handleDataLoaded(data, false)}
                    />
                </TabPane>
                <TabPane tab="Successful Payments" key="2">
                    <Table
                        columns={columns}
                        url='payments?success=true&page=1'
                        onDataLoaded={(data) => handleDataLoaded(data, true)}
                    />
                </TabPane>
                <TabPane tab="All Payments" key="3">
                    <Table
                        columns={columns}
                        url="payments"
                        onDataLoaded={(data) => handleDataLoaded(data, false, true)}
                    />
                </TabPane>
            </Tabs>
        </>
    );
};

export default Payments;
